import { toWalletNumber } from "./walletParsers";
import {
  ApiWalletPreset,
  WalletPresets,
  WalletTopUpIncentive,
  WalletTopUpTier,
} from "./walletTypes";

/**
 * Top-up tiers and incentive maths. Every rule here mirrors the app
 * (wallet_preset_dto.dart, wallet_top_up.controller.dart): the story requires
 * Guest to show exactly the numbers the App shows for the same config.
 */

/** Used until presets load, as the app does. */
export const DEFAULT_MIN_TOP_UP = 100;

/** The top of the range when no tier is configured, as the app does. */
export const DEFAULT_MAX_TOP_UP = 2000;

const isPercentagePoints = (pointsType: unknown) => {
  const normalized = String(pointsType ?? "")
    .trim()
    .toLowerCase();

  return normalized === "percentage" || normalized === "percent";
};

/**
 * A tier is cashback when `creditMultiplier` is positive; otherwise the first
 * incentive action decides between percentage and fixed points. A tier with
 * neither is dropped, as in the app.
 */
export const parseWalletTopUpTier = (
  raw: ApiWalletPreset
): WalletTopUpTier | null => {
  const from = toWalletNumber(raw.from);
  const to = Math.max(from, toWalletNumber(raw.to));
  const action = raw.incentiveActions?.[0];
  const message = action?.metadata?.msg?.trim() || null;
  const base = { id: raw.id, from, to, message };

  const percent = toWalletNumber(raw.creditMultiplier);
  if (percent > 0) {
    return { ...base, incentive: { kind: "cashback", percent } };
  }

  const points = toWalletNumber(action?.metadata?.points);
  if (!action || points <= 0) {
    return null;
  }

  return isPercentagePoints(action.metadata.pointsType)
    ? { ...base, incentive: { kind: "percentagePoints", pointsPer100: points } }
    : { ...base, incentive: { kind: "fixedPoints", points } };
};

export const parseWalletPresets = (raw: {
  currency: string;
  minTopUpAmount?: number | string | null;
  presets?: ApiWalletPreset[] | null;
}): WalletPresets => {
  const min = Number(raw.minTopUpAmount);

  return {
    currency: raw.currency,
    minTopUpAmount: Number.isFinite(min) ? min : DEFAULT_MIN_TOP_UP,
    tiers: (raw.presets ?? [])
      .map(parseWalletTopUpTier)
      .filter((tier): tier is WalletTopUpTier => tier !== null),
  };
};

/**
 * The backend has no maximum; the app caps top-ups at the last tier's upper
 * bound. `Math.max` rather than "last" so an unsorted list gives the same cap.
 */
export const getWalletTopUpLimits = (presets: WalletPresets | null) => ({
  min: presets?.minTopUpAmount ?? DEFAULT_MIN_TOP_UP,
  max: presets?.tiers.length
    ? Math.max(...presets.tiers.map((tier) => tier.to))
    : DEFAULT_MAX_TOP_UP,
});

/** The tier covering `amount`; the last one wins where ranges overlap. */
export const findWalletTierForAmount = (
  amount: number,
  tiers: readonly WalletTopUpTier[]
): WalletTopUpTier | null => {
  let match: WalletTopUpTier | null = null;
  for (const tier of tiers) {
    if (amount >= tier.from && amount <= tier.to) {
      match = tier;
    }
  }

  return match;
};

/**
 * What topping up `amount` earns under `tier`. Sent with the top-up request
 * (`cashback` / `points`) and shown in the success pop-up; the backend does not
 * recompute it.
 */
export const calculateWalletIncentive = (
  amount: number,
  tier: WalletTopUpTier | null
): WalletTopUpIncentive => {
  if (!tier || amount <= 0) {
    return { cashback: null, points: null };
  }

  const { incentive } = tier;
  switch (incentive.kind) {
    case "cashback":
      // To the cent: the backend credits the cashback exactly as sent, so an
      // unrounded 26.666000000000004 would leave a fractional-cent balance.
      return {
        cashback: Math.round(amount * incentive.percent) / 100,
        points: null,
      };
    case "percentagePoints":
      return {
        cashback: null,
        points: Math.floor((amount * incentive.pointsPer100) / 100),
      };
    case "fixedPoints":
      return { cashback: null, points: incentive.points };
  }
};

/** The incentive for a tier's own preset card ("EGP 100 · Earn +EGP 20"). */
export const calculateWalletTierPresetIncentive = (tier: WalletTopUpTier) =>
  calculateWalletIncentive(tier.from, tier);
